import { api, buildQuery, type Page } from "./api";
import type { Ticket } from "./tickets.service";

const PORTAL_TOKEN_KEY = "cysol_portal_token";

export interface PortalLoginResponse {
  access_token: string;
  contact_id: number;
  contact_name: string;
  client_id: number;
  client_name: string;
}

export interface PortalContactProfile {
  contact_id: number;
  contact_name: string;
  client_id: number;
  client_name: string;
}

export interface PortalPlant {
  id: number;
  name: string;
  location: string | null;
}

export interface PortalCategory {
  id: number;
  name: string;
  department_name: string | null;
}

/** Format ticket id as TK-001 */
export function formatTicketNumber(id: number): string {
  return `TK-${String(id).padStart(3, '0')}`;
}

/** Portal público: el ID debe ingresarse como TK-123 (TK insensible a mayúsculas). */
export function parseTicketPublicId(raw: string): { ok: true; id: number } | { ok: false; message: string } {
  const s = raw.trim().replace(/^#/, '');
  if (!s) return { ok: false, message: 'Ingresá el número de ticket.' };
  const m = s.match(/^TK-(\d+)$/i);
  if (!m) {
    if (/^\d+$/.test(s)) {
      return {
        ok: false,
        message: 'Usá el formato TK-000 (ej.: TK-001), no solo el número.',
      };
    }
    return { ok: false, message: 'Formato inválido. Ejemplo: TK-001' };
  }
  const id = Number(m[1]);
  if (!Number.isSafeInteger(id) || id < 1) {
    return { ok: false, message: 'Número de ticket no válido.' };
  }
  return { ok: true, id };
}

export interface PortalAttachment {
  id: number;
  name: string;
  size_bytes: number | null;
  file_path: string;
  file_type: string | null;
}

export interface PortalTicketTracking {
  id: number;
  ticket_number?: string;
  title: string;
  description: string;
  status_key: string;
  status_label: string;
  priority_key: string;
  priority_label: string;
  category_name: string;
  plant_name: string;
  client_name: string;
  created_at: string;
  updated_at: string | null;
  resolved_at: string | null;
  attachments: PortalAttachment[];
  timeline: {
    id: number;
    description: string;
    timestamp: string;
    comment: string | null;
  }[];
}

export interface PortalTicketCreate {
  title: string;
  description: string;
  plant_id: number;
  category_id?: number;
  priority_key?: string;
}

export interface ValidateEmailResponse {
  contact_id: number;
  contact_name: string;
  client_id: number;
  client_name: string;
  plants: { id: number; name: string; location: string | null }[];
}

export interface PortalTicketSubmit {
  email: string;
  plant_id: number;
  title: string;
  description?: string;
  category_id?: number;
  priority_key?: string;
}

export interface PortalTicketReview {
  ticket_number: string;
  title: string;
  status_key: string;
  status_label: string | null;
  plant_name: string | null;
  category_name: string | null;
  created_at: string;
}

export function getPortalToken(): string | null {
  return localStorage.getItem(PORTAL_TOKEN_KEY);
}

export function setPortalToken(token: string) {
  localStorage.setItem(PORTAL_TOKEN_KEY, token);
}

export function clearPortalToken() {
  localStorage.removeItem(PORTAL_TOKEN_KEY);
}

export const portalService = {
  async login(email: string, password: string): Promise<PortalLoginResponse> {
    const res = await api.post<PortalLoginResponse>("/portal/auth/login", { email, password });
    setPortalToken(res.access_token);
    return res;
  },

  logout() {
    clearPortalToken();
  },

  getProfile(): Promise<PortalContactProfile> {
    return api.get("/portal/tickets/me", getPortalToken() || undefined);
  },

  listPlants(): Promise<PortalPlant[]> {
    return api.get("/portal/tickets/plants", getPortalToken() || undefined);
  },

  listCategories(): Promise<PortalCategory[]> {
    return api.get("/portal/tickets/categories", getPortalToken() || undefined);
  },

  listTickets(filters: { skip?: number; limit?: number; status_key?: string; priority_key?: string } = {}): Promise<Page<Ticket>> {
    return api.get(`/portal/tickets/${buildQuery(filters)}`, getPortalToken() || undefined);
  },

  createTicket(data: PortalTicketCreate): Promise<Ticket> {
    return api.post("/portal/tickets/", data, getPortalToken() || undefined);
  },

  /** Validate contact by email only — for submit form. No password. */
  validateEmail(email: string): Promise<ValidateEmailResponse> {
    return api.post("/portal/auth/validate-email", { email: email.trim().toLowerCase() } as { email: string });
  },

  /** Submit ticket with email only — no auth. For public submit form. */
  submitTicket(data: PortalTicketSubmit): Promise<Ticket> {
    return api.post("/portal/tickets/submit", {
      ...data,
      email: data.email.trim().toLowerCase(),
    });
  },

  /** Submit ticket with optional file attachments. Uses multipart. */
  async submitTicketWithAttachments(data: PortalTicketSubmit, files: File[]): Promise<Ticket> {
    const fd = new FormData();
    fd.append("email", data.email.trim().toLowerCase());
    fd.append("plant_id", String(data.plant_id));
    fd.append("title", data.title);
    fd.append("description", data.description || "");
    fd.append("priority_key", data.priority_key || "medium");
    if (data.category_id != null) fd.append("category_id", String(data.category_id));
    for (const f of files) fd.append("files", f);
    const base = import.meta.env.VITE_API_BASE_URL || "/api/v1";
    const url = `${base.replace(/\/$/, "")}/portal/tickets/submit-multipart`;
    const res = await fetch(url, { method: "POST", body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw { status: res.status, message: (typeof err.detail === "string" ? err.detail : err.detail?.msg) || res.statusText };
    }
    return res.json();
  },

  listCategoriesPublic(): Promise<PortalCategory[]> {
    return api.get("/portal/tickets/categories-public");
  },

  trackTicket(ticketId: number): Promise<PortalTicketTracking> {
    return api.get(`/portal/tickets/${ticketId}`, getPortalToken() || undefined);
  },

  /** Get estimated resolution hours for category+priority (no auth required). */
  getEstimatedResolution(categoryId: number | null, priorityKey: string): Promise<{ resolution_time_hours: number }> {
    const params = new URLSearchParams({ priority_key: priorityKey });
    if (categoryId != null) params.set('category_id', String(categoryId));
    return api.get(`/portal/tickets/estimated-resolution?${params}`);
  },

  /** PUBLIC — fetch limited ticket info for the portal-review page. No auth. */
  getTicketReview(ticketId: number): Promise<PortalTicketReview> {
    return api.get(`/portal/tickets/${ticketId}/review`);
  },

  /** PUBLIC — contact confirms a closed ticket was resolved. No auth. */
  confirmTicket(ticketId: number): Promise<{ ok: boolean }> {
    return api.post(`/portal/tickets/${ticketId}/confirm`, {});
  },

  /** PUBLIC — contact requests to reopen a closed ticket. No auth. */
  reopenTicket(ticketId: number, comment?: string): Promise<{ ok: boolean }> {
    return api.post(`/portal/tickets/${ticketId}/reopen`, { comment: comment ?? null });
  },
};
